import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { getDatasetVersion } from "../../../api/knowledgeData/knowledgeDataApi";
import type { DatasetVersionSummary } from "../../../api/knowledgeData/knowledgeDataDecoders";
import { useLoad } from "../../../shared/loading/useLoad";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import ReleaseChecksPanel from "./ReleaseChecksPanel";
import AppButton from "../../../ui/buttons/AppButton";

/**
 * A version's release checks (D4): whether it was checked as a whole, and on request the
 * report stored with it. A version made before the release checks says so: it stays valid
 * but cannot be exported.
 */
export default function VersionChecks({ datasetId, version }: { datasetId: string; version: DatasetVersionSummary }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (version.checksStatus === null) {
    return <p className="app-text-muted text-xs">{t("data.releaseChecks.unchecked")}</p>;
  }
  return (
    <div className="space-y-1">
      <AppButton variant="ghost" size="xs" aria-expanded={open} onClick={() => { setOpen(!open); }}>
        {open ? t("data.releaseChecks.hide") : t("data.releaseChecks.show", { status: t(`data.releaseChecks.status.${version.checksStatus}`), version: version.checkedAgainst ?? "" })}
      </AppButton>
      {open ? <LoadedChecks datasetId={datasetId} number={version.number} /> : null}
    </div>
  );
}

function LoadedChecks({ datasetId, number }: { datasetId: string; number: number }) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => getDatasetVersion(datasetId, number, signal), [datasetId, number]);
  const version = useLoad(load);
  if (version.state.status !== "loaded") return <LoadStatus state={version.state} loadingLabel={t("common.loading")} onRetry={version.reload} compact />;
  const checks = version.state.value.checks;
  return checks === null ? null : <ReleaseChecksPanel checks={checks} />;
}
